<template>
  <HgModal
    v-model="showModal"
    :aria-label="t('activitySharing.modal.title')"
    closeButtonCssClass="activity-sharing-modal-close"
    data-test="activity-sharing-modal"
    @keydown="handleKeydown"
  >
    <template #title>{{ t('activitySharing.modal.title') }}</template>

    <div ref="content" :class="$style.content">
      <p>{{ t('activitySharing.modal.intro', { platform }) }}</p>

      <div>
        <div :class="$style.sharingCard">
          <div :class="$style.sharingSetting">
            <div>
              <h4 :id="toggleLabelId">
                {{ t('activitySharing.modal.toggleLabel') }}
              </h4>
              <p :class="$style.secondaryText">
                {{ t('activitySharing.modal.toggleDescription') }}
              </p>
            </div>
            <HgToggle
              v-model="isSharingEnabled"
              theme="secondary"
              name="shareActivitySummary"
              :class="[$style.fixedSize, $style.toggle]"
              :labelId="toggleLabelId"
            />
          </div>
          <p :class="$style.secondaryText">
            {{ t('activitySharing.modal.requestSentEitherWay') }}
          </p>
        </div>
        <p :class="[$style.scopeNote, $style.secondaryText]">
          {{ t('activitySharing.modal.scopeNote') }}
        </p>
      </div>

      <section :aria-labelledby="sharedHeadingId">
        <h4 :id="sharedHeadingId">{{ t('activitySharing.modal.sharedHeading') }}</h4>
        <ul :class="$style.sharedItems">
          <li v-for="item in sharedItems" :key="item.key" :class="$style.sharedItem">
            <HgIcon :icon="item.icon" :class="$style.fixedSize" color="gray" aria-hidden="true" />
            <div>
              <h4>{{ t(`activitySharing.modal.${item.key}.title`) }}</h4>
              <p>{{ t(`activitySharing.modal.${item.key}.description`) }}</p>
            </div>
          </li>
        </ul>
      </section>

      <p :class="$style.secondaryText">
        {{ t('activitySharing.modal.aggregationNote') }}
      </p>

      <div :class="$style.explanations">
        <HgCollapsible
          :id="meaningId"
          :themeClass="$style.disclosureTheme"
          :iconCssClass="$style.disclosureIcon"
          :bodyCssClass="$style.explanationContent"
          @expanded="isMeaningExpanded = true"
          @collapsed="isMeaningExpanded = false"
        >
          <template #header>{{ t('activitySharing.modal.meaning.title') }}</template>
          <template v-if="isMeaningExpanded" #default>
            <p>{{ t('activitySharing.modal.meaning.location') }}</p>
            <p>{{ t('activitySharing.modal.meaning.propertyDetails') }}</p>
            <p>{{ t('activitySharing.modal.meaning.history') }}</p>
          </template>
        </HgCollapsible>

        <HgCollapsible
          :id="sellingId"
          :themeClass="$style.disclosureTheme"
          :iconCssClass="$style.disclosureIcon"
          :bodyCssClass="$style.explanationContent"
          @expanded="isSellingExpanded = true"
          @collapsed="isSellingExpanded = false"
        >
          <template #header>{{ t('activitySharing.modal.selling.title') }}</template>
          <template v-if="isSellingExpanded" #default>
            <i18n-t keypath="activitySharing.modal.selling.consent" tag="p" scope="global">
              <template #platform>{{ platform }}</template>
              <template #contactForm>
                <HgLink :href="contactUrl" target="_blank" rel="noopener">
                  {{ t('activitySharing.modal.selling.contactForm') }}
                </HgLink>
              </template>
            </i18n-t>
            <i18n-t keypath="activitySharing.modal.selling.separateConsent" tag="p" scope="global">
              <template #privacyPolicy>
                <HgLink :href="t('TnCText.ds.link')" target="_blank" rel="noopener">
                  {{ t('TnCText.ds.name') }}
                </HgLink>
              </template>
            </i18n-t>
          </template>
        </HgCollapsible>
      </div>
    </div>

    <template #footerAction>
      <HgLink :href="t('TnCText.ds.link')" target="_blank" rel="noopener">
        {{ t('activitySharing.modal.privacyPolicy') }}
      </HgLink>
    </template>
    <template #footer>
      <HgButton type="button" theme="secondary" @click="handleClose">
        {{ t('activitySharing.modal.done') }}
      </HgButton>
    </template>
  </HgModal>
</template>

<script setup lang="ts">
import { HgButton, HgCollapsible, HgIcon, HgLink, HgModal, HgToggle } from '@smg-real-estate/domus';
import { computed, nextTick, onBeforeUnmount, onMounted, onUnmounted, ref, useId } from 'vue';
import { useI18n } from 'vue-i18n';

import { InteractionEvent, InteractionGoal } from '@/helpers/GTM/GTMParameters';
import { trackEventHarmonized } from '@/helpers/GTM/GTMTrack';
import { isPlatformHg } from '@/helpers/platformDetection';

defineOptions({ name: 'ActivitySharingModal' });

const props = defineProps<{
  isVisible: boolean;
  sharingEnabled: boolean;
}>();
const isSharingEnabled = ref(props.sharingEnabled);
const emit = defineEmits<{ close: [isSharingEnabled: boolean] }>();
const showModal = computed({
  get: () => props.isVisible,
  set: (isVisible: boolean) => {
    if (!isVisible) handleClose();
  },
});
const { t } = useI18n();
const platform = isPlatformHg() ? 'Homegate' : 'ImmoScout24';
const contactUrl = computed(
  () =>
    new URL(
      t('activitySharing.modal.selling.contactPath'),
      isPlatformHg() ? 'https://www.homegate.ch' : 'https://www.immoscout24.ch',
    ).href,
);
const toggleLabelId = useId();
const sharedHeadingId = useId();
const meaningId = useId();
const sellingId = useId();
const isMeaningExpanded = ref(false);
const isSellingExpanded = ref(false);
const sharedItems = [
  { key: 'inquiries', icon: 'Email' },
  { key: 'search', icon: 'Search' },
  { key: 'account', icon: 'PersonOutline' },
] as const;

const content = ref<HTMLElement>();
const dialogSelector = '[role="dialog"]';
let previousFocus: HTMLElement | null = null;
let parentDialog: HTMLElement | null = null;
let previousScrollLock:
  { overflow: string; paddingRight: string; scrollLocked: string } | undefined;

const labelCloseButton = (): void => {
  content.value
    ?.closest(dialogSelector)
    ?.querySelector('.activity-sharing-modal-close')
    ?.setAttribute('aria-label', t('activitySharing.modal.close'));
};

const handleClose = (): void => {
  emit('close', isSharingEnabled.value);
};

onMounted(async () => {
  trackEventHarmonized(InteractionEvent.FEATURE_DISPLAYED, {
    h_event_action_007: InteractionGoal.DISPLAY_ACTIVITY_SHARING_MODAL,
  });
  previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  parentDialog = previousFocus?.closest<HTMLElement>(dialogSelector) ?? null;

  await nextTick();
  const dialog = content.value?.closest<HTMLElement>(dialogSelector);
  labelCloseButton();
  dialog?.focus();
});

onBeforeUnmount(() => {
  // Capture Domus's delayed lock before the child modal clears it.
  if (parentDialog && document.body.dataset.scrollLocked !== undefined) {
    previousScrollLock = {
      overflow: document.body.style.overflow,
      paddingRight: document.body.style.paddingRight,
      scrollLocked: document.body.dataset.scrollLocked,
    };
  }
});

onUnmounted(() => {
  // Domus clears the global scroll lock even when a parent modal is still open.
  if (parentDialog?.isConnected && previousScrollLock) {
    document.body.style.overflow = previousScrollLock.overflow;
    document.body.style.paddingRight = previousScrollLock.paddingRight;
    document.body.dataset.scrollLocked = previousScrollLock.scrollLocked;
  }
  previousFocus?.focus();
});

const handleKeydown = (event: KeyboardEvent): void => {
  if (event.key === 'Escape') {
    event.stopPropagation();
    handleClose();
    return;
  }
  if (event.key !== 'Tab') return;

  const dialog = event.currentTarget as HTMLElement;
  const controls = dialog.querySelectorAll<HTMLElement>(
    'a[href], button:not([disabled]), input:not([disabled]), [tabindex="0"]',
  );
  const first = controls[0];
  const last = controls[controls.length - 1];
  const active = document.activeElement;
  const isFocusOnControl = Array.from(controls).includes(active as HTMLElement);
  if (!isFocusOnControl || (event.shiftKey ? active === first : active === last)) {
    event.preventDefault();
    (event.shiftKey ? last : first)?.focus();
  }
};
</script>

<style lang="scss" module>
@import '@/assets/variables';

.content {
  @include body-2;

  display: flex;
  flex-direction: column;
  gap: $space-150;

  p,
  h4 {
    margin: 0;
  }

  h4 {
    @include body-2-bold;
  }
}

.sharingCard {
  display: flex;
  flex-direction: column;
  gap: $space-75;
  padding: $space-100;
  background: var(--message-color-info-background);
  border-radius: $border-radius;
}

.sharingSetting {
  display: flex;
  gap: $space-100;
  align-items: flex-start;
  justify-content: space-between;
}

.fixedSize {
  flex-shrink: 0;
}

.toggle {
  position: relative;

  > span {
    inset: 0;
  }

  &:focus-within {
    outline: auto;
    outline-offset: $space-25;
  }
}

.secondaryText {
  @include body-3;

  color: var(--text-color-secondary);
}

.content .scopeNote {
  margin-top: $space-50;
}

.sharedItems {
  display: flex;
  flex-direction: column;
  gap: $space-100;
  padding: 0;
  margin: $space-100 0 0;
  list-style: none;
}

.sharedItem {
  display: flex;
  gap: $space-100;
  align-items: flex-start;
}

.explanations {
  display: flex;
  flex-direction: column;
  gap: $space-100;
  padding-top: $space-100;
  border-top: 1px solid $color-grey-40;
}

.disclosureTheme {
  color: var(--base-color-link);
  fill: var(--base-color-link);
}

.disclosureIcon {
  order: -1;
  flex-shrink: 0;
}

.explanationContent {
  display: flex;
  flex-direction: column;
  gap: $space-100;

  p:first-child {
    margin-top: $space-100;
  }
}
</style>
